import type {
  PDFClassification,
  ScoreTimeline,
  ScoreStatus,
  Score,
  ScoreSourceType,
} from '@/types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Error carrying the backend's X-Trace-ID (backend/app/main.py's TraceIDMiddleware sets
 * this on every response), so error UIs can display an id that greps directly into
 * backend logs -- see backend/app/core/logging.py's trace_id_var/JSON formatter.
 */
export class ApiError extends Error {
  traceId?: string;

  constructor(message: string, traceId?: string) {
    super(message);
    this.name = 'ApiError';
    this.traceId = traceId;
  }
}

function getTraceId(response: Response): string | undefined {
  return response.headers.get('X-Trace-ID') ?? undefined;
}

/**
 * Classify a PDF file to determine quality and expected accuracy
 */
export async function classifyPDF(file: File): Promise<PDFClassification> {
  const formData = new FormData();
  formData.append('file', file);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30_000);

  try {
    const response = await fetch(`${API_BASE}/pdf/classify`, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });
    const traceId = getTraceId(response);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Classification failed' }));
      throw new ApiError(error.detail || 'Classification failed', traceId);
    }

    return response.json();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('PDF classification timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Upload a score file for processing
 */
export async function uploadScore(
  file: File,
  onProgress?: (progress: number) => void,
  options?: { isScan?: boolean }
): Promise<{
  scoreId: string;
  status: ScoreStatus;
  musicXmlUrl?: string;
  timeline?: ScoreTimeline;
  traceId?: string;
}> {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.isScan) {
    formData.append('is_scan', 'true');
  }

  onProgress?.(10);

  const response = await fetch(`${API_BASE}/scores/upload`, {
    method: 'POST',
    body: formData,
  });
  const traceId = getTraceId(response);

  onProgress?.(50);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new ApiError(error.detail || 'Upload failed', traceId);
  }

  const result = await response.json();
  onProgress?.(80);

  // If processing is async, poll for status
  if (result.status === 'processing') {
    return await pollProcessingStatus(result.id, onProgress, traceId);
  }

  onProgress?.(100);
  return {
    scoreId: result.id ?? result.scoreId,
    status: result.status,
    musicXmlUrl: result.musicxml_url ?? result.musicXmlUrl,
    timeline: result.timeline,
    traceId,
  };
}

/**
 * Poll for processing status until complete
 */
async function pollProcessingStatus(
  scoreId: string,
  onProgress?: (progress: number) => void,
  initialTraceId?: string
): Promise<{
  scoreId: string;
  status: ScoreStatus;
  musicXmlUrl?: string;
  timeline?: ScoreTimeline;
  traceId?: string;
}> {
  // Poll until server reports terminal state (ready/error/needs_correction).
  // 30-minute absolute wall-clock cap as escape hatch — well beyond any legit
  // OMR run; stuck tasks should be terminated server-side via Celery
  // soft_time_limit (process_score: 600s) which writes status='error' to DB.
  const startedAt = Date.now();
  const ABSOLUTE_TIMEOUT_MS = 30 * 60 * 1000;
  // Sticks to the upload request's trace id, not each poll's own. The frontend never
  // sends X-Trace-ID back on subsequent requests, so backend/app/main.py's
  // TraceIDMiddleware mints an unrelated fresh id per poll -- only the upload's id is
  // the one Celery actually inherited (tasks.py's trace_id_var propagation) and that
  // shows up correlated across the real processing pipeline's log lines.
  let traceId = initialTraceId;

  while (Date.now() - startedAt < ABSOLUTE_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const response = await fetch(`${API_BASE}/scores/${scoreId}/status`);
    traceId = traceId ?? getTraceId(response);
    if (!response.ok) {
      throw new ApiError('Failed to check status', traceId);
    }

    const status = await response.json();
    if (typeof status.progress === 'number') {
      onProgress?.(status.progress);
    }

    if (status.status === 'ready' || status.status === 'needs_correction') {
      // Fetch the full score data
      const scoreResponse = await fetch(`${API_BASE}/scores/${scoreId}`);
      traceId = traceId ?? getTraceId(scoreResponse);
      if (!scoreResponse.ok) {
        const errorBody = await scoreResponse
          .json()
          .catch(() => ({ detail: scoreResponse.statusText }));
        console.error(
          '[api/pollStatus] Failed: fetch score | reason=',
          errorBody.detail || `HTTP ${scoreResponse.status}`,
          '| score_id=',
          scoreId
        );
        throw new ApiError(errorBody.detail || `HTTP ${scoreResponse.status}`, traceId);
      }
      const score = await scoreResponse.json();
      onProgress?.(95);

      // Fetch the timeline data from dedicated endpoint
      let timeline: ScoreTimeline | undefined;
      const timelineResponse = await fetch(`${API_BASE}/scores/${scoreId}/timeline`);
      traceId = traceId ?? getTraceId(timelineResponse);
      if (timelineResponse.ok) {
        timeline = await timelineResponse.json();
      } else {
        console.error(
          '[api/pollStatus] Failed: timeline fetch | reason=HTTP',
          timelineResponse.status,
          '| score_id=',
          scoreId
        );
        throw new ApiError(`Timeline fetch failed: HTTP ${timelineResponse.status}`, traceId);
      }

      onProgress?.(100);
      return {
        scoreId: score.id,
        status: score.status,
        musicXmlUrl: score.musicxml_url,
        timeline,
        traceId,
      };
    }

    if (status.status === 'error') {
      throw new ApiError(status.error || status.message || 'Processing failed', traceId);
    }
  }

  throw new ApiError('Processing timeout (30 min wall-clock cap reached)', traceId);
}

/**
 * Get a score by ID
 */
export async function getScore(scoreId: string) {
  const response = await fetch(`${API_BASE}/scores/${scoreId}`);

  if (!response.ok) {
    throw new Error('Score not found');
  }

  return response.json();
}

/**
 * Get all scores
 */
interface ScoreApiResponse {
  id: string;
  title: string;
  composer: string;
  file_type: string;
  status: string;
  pdf_url?: string;
  musicxml_url?: string;
  pdf_classification?: PDFClassification;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export async function getScores(): Promise<Score[]> {
  const response = await fetch(`${API_BASE}/scores/`);
  if (!response.ok) {
    throw new Error('Failed to fetch scores');
  }
  const scores: ScoreApiResponse[] = await response.json();
  return scores.map((score) => ({
    id: score.id,
    title: score.title,
    composer: score.composer,
    sourceType: score.file_type as ScoreSourceType,
    status: score.status as ScoreStatus,
    originalFileUrl: score.pdf_url || score.musicxml_url || undefined,
    musicXmlUrl: score.musicxml_url,
    timeline: undefined,
    pdfClassification: score.pdf_classification,
    errorMessage: score.error_message,
    createdAt: score.created_at,
    updatedAt: score.updated_at,
  }));
}

/**
 * Fetch timeline data for a score
 */
export async function getTimeline(scoreId: string): Promise<ScoreTimeline> {
  const response = await fetch(`${API_BASE}/scores/${scoreId}/timeline`);
  if (!response.ok) {
    console.error('[api/getTimeline] Failed: HTTP', response.status, '| score_id=', scoreId);
    throw new Error(`Timeline fetch failed: HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Delete a score
 */
export async function deleteScore(scoreId: string) {
  const response = await fetch(`${API_BASE}/scores/${scoreId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete score');
  }
}

/**
 * Submit corrections for a score
 */
export async function submitCorrections(
  scoreId: string,
  corrections: Array<{
    noteId: string;
    type: string;
    newValue: unknown;
  }>
) {
  const response = await fetch(`${API_BASE}/scores/${scoreId}/corrections`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ corrections }),
  });

  if (!response.ok) {
    throw new Error('Failed to submit corrections');
  }

  return response.json();
}
